import React from "react";
import { Link } from "react-router-dom";
import CTAStrip from "@/components/CTAStrip";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";

const VALUES = [
  {
    n: "01",
    t: "Own the funnel",
    d: "We build and run our own properties. No daisy-chained brokers, no recycled inventory, no surprises.",
  },
  {
    n: "02",
    t: "Treat compliance as design",
    d: "Consent, retention and auditability are wireframed before a single line of copy is written.",
  },
  {
    n: "03",
    t: "Price to bind",
    d: "Volume without a bind rate is noise. Every campaign we run is benchmarked to your back-end.",
  },
  {
    n: "04",
    t: "Move like an investor",
    d: "We re-balance your spend weekly. If a vertical is underperforming, we move the budget — not just the dashboard.",
  },
];

const TIMELINE = [
  { y: "2008", t: "Founded in Los Angeles as a direct response agency." },
  { y: "2012", t: "Launched our first owned-and-operated insurance brand." },
  { y: "2016", t: "Reached 1M annual leads delivered." },
  { y: "2019", t: "Compliance & TCPA platform built in-house." },
  { y: "2022", t: "Expanded into Solar and Home Services verticals." },
  { y: "Today", t: "8.2M+ annual leads across eight verticals nationwide." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHead
        title="About"
        description="An LA agency that buys media like operators — because we are. Fifteen years building owned-and-operated lead generation across eight verticals."
        path="/about"
      />
      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">About Citirex</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[110px]">
              An LA agency that{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                buys media like operators
              </span>{" "}
              — because we are.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:gap-16 md:px-12 md:py-32">
          <div className="md:col-span-7">
            <div className="relative aspect-[4/3] w-full overflow-hidden">
              <img
                src="https://images.pexels.com/photos/6476256/pexels-photo-6476256.jpeg"
                alt="Citirex team collaborating"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-5">
            <p className="overline">Our story</p>
            <p className="mt-6 font-editorial text-2xl italic leading-snug text-citirex-ink md:text-3xl">
              We started Citirex in a Downtown LA loft in 2008 with one
              belief: lead generation should be engineered, not negotiated.
            </p>
            <p className="mt-6 font-body text-base text-citirex-sub md:text-lg">
              Fifteen years later, we run owned-and-operated funnels across
              eight of the largest performance marketing verticals in the
              U.S. Our partners include national carriers, insurance IMOs,
              top-10 solar installers and regional home-services
              contractors. We don't aggregate. We don't broker. We build the
              demand — and stand behind it.
            </p>
            <Link
              to="/contact"
              className="mt-10 inline-flex items-center gap-2 border-2 border-citirex-ink px-6 py-4 font-body text-sm font-semibold uppercase tracking-wider text-citirex-ink transition-colors hover:bg-citirex-ink hover:text-citirex-inv"
            >
              Talk to the team →
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-citirex-border bg-citirex-bg2">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <p className="overline">Operating principles</p>
          <div className="mt-10 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.n} className="bg-citirex-bg2 p-8">
                <p className="font-editorial text-sm italic text-citirex-sub">
                  {v.n}
                </p>
                <h3 className="mt-3 font-display text-2xl font-medium text-citirex-ink">
                  {v.t}
                </h3>
                <p className="mt-3 font-body text-sm text-citirex-sub">
                  {v.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="overline">Timeline</p>
              <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Fifteen years,{" "}
                <span className="font-editorial italic">
                  one playbook
                </span>
                .
              </h2>
            </div>
            <div className="md:col-span-7">
              <ul className="border-t border-citirex-border">
                {TIMELINE.map((t) => (
                  <li
                    key={t.y}
                    className="grid grid-cols-12 items-start gap-6 border-b border-citirex-border py-6"
                  >
                    <span className="col-span-3 font-editorial text-2xl italic text-citirex-accent md:text-3xl">
                      {t.y}
                    </span>
                    <span className="col-span-9 font-body text-base text-citirex-ink md:text-lg">
                      {t.t}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CTAStrip
        eyebrow="Built in Los Angeles"
        title="Hire the agency that operates like an owner."
        buttonText="Talk to sales"
      />
    </main>
  );
}
